import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { setContentFilterData } from 'approot/shared/filters/filters.apollo';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { Box } from 'approot/shared/styles/box.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import { spacing24 } from 'approot/shared/styles/spacing.style';
import {
  PUBLIC_ABOUT,
  PUBLIC_BLOG,
  PUBLIC_LESSONS,
  PUBLIC_PRICING,
  PUBLIC_PRICING_FAQS,
  PUBLIC_SCHOOL_BENEFITS,
  PUBLIC_SCOPE_AND_SEQUENCE,
  PUBLIC_STIMULUS,
  PUBLIC_SUBJECT,
  PUBLIC_TEACHER_BENEFITS,
} from 'global/public-routes.constants';
import { NAMESPACE_SUPPORT } from 'internationalise';
import { INTERCOM_LAUNCH_TARGET_CLASS } from 'lib/intercom/intercom';
import {
  useDrawerLayout,
  useDrawerScrollTop,
  useLayoutLinks,
} from '../../layout.hooks';
import {
  NAV_ABOUT_LABEL,
  NAV_DISCIPLINES_LABEL,
  NAV_EXPLORE,
  NAV_EXPLORE_INQUISITIVE_LABEL,
  NAV_FAQS_LABEL,
  NAV_FOR_SCHOOLS_LABEL,
  NAV_FOR_TEACHERS_LABEL,
  NAV_LESSONS,
  NAV_LESSONS_LABEL,
  NAV_PRICING_LABEL,
  NAV_PRODUCT,
  NAV_PRODUCT_LABEL,
  NAV_SCOPE_AND_SEQUENCE_LABEL,
  NAV_SEND_MESSAGE_LABEL,
  NAV_STIMULUS_LIBRARY_LABEL,
  NAV_SUPPORT,
  NAV_SUPPORT_LABEL,
  NAV_TEACHING_BLOG_LABEL,
} from '../mega/mega-nav.constants';
import {
  JOIN_LINK,
  pushNavSublinkDataEvent,
  pushSideNavFollowDataEvent,
  SIGN_IN_LINK,
} from '../navigation.utils';
import * as Styled from './mini-nav.style';
import { useYearTranslation } from 'approot/shared/logic/years';
import {
  openChooseUserJoinModal,
  openChooseUserSignInModal,
} from 'approot/global-notifications/global-notification.utils';

type Props = {
  isActive: boolean;
  onClickMenu: () => void;
};

const MiniNav = ({ isActive, onClickMenu }: Props) => {
  const navigate = useNavigate();
  const {
    isRegionAU,
    regionBanner: { height },
  } = useRegionData();
  const { t } = useTranslation(NAMESPACE_SUPPORT);
  const { getYearLabel } = useYearTranslation();

  const yearsLabel = useMemo(() => {
    return getYearLabel({ plural: true });
  }, [getYearLabel]);

  const yearLabel = useMemo(() => {
    return getYearLabel({ plural: false });
  }, [getYearLabel]);

  const { goToFaqs } = useLayoutLinks();

  const navRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useDrawerLayout(navRef, isActive, true, onClickMenu);
  useDrawerScrollTop(navRef, scrollRef, isActive);

  const { data, disciplines, yearOptions } = useGetStartupData();
  const subjects = data?.startupData.subjects;

  const handleLinkClick = useCallback(
    (menu: string, label: string) => {
      pushNavSublinkDataEvent(menu, label);
      onClickMenu();
    },
    [onClickMenu]
  );

  return (
    <Styled.MiniNav
      $isActive={isActive}
      aria-label="Primary navigation"
      tabIndex={-1}
      ref={navRef}
      bannerHeight={height}
    >
      <Styled.MiniNavHeader>
        <Styled.MiniNavCloseButton
          aria-label="Close navigation"
          onClick={onClickMenu}
        >
          Close
        </Styled.MiniNavCloseButton>
      </Styled.MiniNavHeader>
      <Styled.MiniNavScrollContainer ref={scrollRef}>
        <Styled.MiniNavLinksWrapper>
          <Box $justifyContent="space-between">
            <Styled.MiniNavHeading>
              Browse by {disciplines ? NAV_DISCIPLINES_LABEL : 'Subject'}
            </Styled.MiniNavHeading>
            <Styled.MiniNavViewAll
              to={`${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`}
              onClick={e => {
                e.preventDefault();

                setContentFilterData({
                  yearFilter: [],
                  curriculumFilter: [],
                  subjectFilter: [],
                  curriculumVersionFilter: [],
                  triggerContentRefresh: true,
                });
                handleLinkClick(
                  NAV_LESSONS,
                  `Browse by ${
                    disciplines ? NAV_DISCIPLINES_LABEL : NAV_LESSONS_LABEL
                  }`
                );
                navigate(`${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`);
              }}
            >
              See All
            </Styled.MiniNavViewAll>
          </Box>

          <Box $flexWrap="wrap">
            {disciplines?.map(discipline => (
              <Styled.MiniNavLink
                key={discipline.name}
                to={discipline.slug}
                onClick={() => {
                  setContentFilterData({
                    triggerContentRefresh: true,
                  });
                  handleLinkClick(NAV_LESSONS, discipline.label);
                }}
              >
                {discipline.label}
              </Styled.MiniNavLink>
            ))}
            {!disciplines &&
              subjects?.map(subject => (
                <Styled.MiniNavLink
                  key={subject.name}
                  to={generatePath(PUBLIC_SUBJECT, {
                    subject: subject.name,
                  })}
                  onClick={() => {
                    setContentFilterData({
                      triggerContentRefresh: true,
                    });
                    handleLinkClick(NAV_LESSONS, subject.label);
                  }}
                >
                  {subject.label}
                </Styled.MiniNavLink>
              ))}
          </Box>
          <HrStyled marginTop={18} marginBottom={spacing24} />

          <Box $justifyContent="space-between">
            <Styled.MiniNavHeading>Browse by {yearLabel}</Styled.MiniNavHeading>
            <Styled.MiniNavViewAll
              to={`${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`}
              aria-label={`Browse All ${yearsLabel}`}
              onClick={e => {
                e.preventDefault();

                setContentFilterData({
                  yearFilter: [],
                  curriculumFilter: [],
                  subjectFilter: [],
                  curriculumVersionFilter: [],
                  triggerContentRefresh: true,
                });
                handleLinkClick(NAV_LESSONS, `All ${yearsLabel}`);
                navigate(`${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`);
              }}
            >
              See All
            </Styled.MiniNavViewAll>
          </Box>
          <Box $flexWrap="wrap">
            {yearOptions?.map(year => (
              <Styled.MiniNavLink
                to={`${PUBLIC_LESSONS}?years=${year.value}${
                  isRegionAU ? '&view=units' : ''
                }`}
                key={year.value}
                aria-label={`Browse by ${year.label}`}
                onClick={e => {
                  e.preventDefault();

                  setContentFilterData({
                    yearFilter: [year.value],
                    curriculumFilter: [],
                    subjectFilter: [],
                    curriculumVersionFilter: [],
                    triggerContentRefresh: true,
                  });
                  handleLinkClick(NAV_LESSONS, year.label);
                  navigate(
                    `${PUBLIC_LESSONS}?years=${year.value}${
                      isRegionAU ? '&view=units' : ''
                    }`
                  );
                }}
              >
                {year.label}
              </Styled.MiniNavLink>
            ))}
          </Box>
          <HrStyled marginTop={18} marginBottom={spacing24} />

          <Styled.MiniNavHeading>
            {NAV_EXPLORE_INQUISITIVE_LABEL}
          </Styled.MiniNavHeading>
          <Box $flexWrap="wrap">
            <Styled.MiniNavLink
              to={PUBLIC_STIMULUS}
              onClick={() =>
                handleLinkClick(NAV_EXPLORE, NAV_STIMULUS_LIBRARY_LABEL)
              }
            >
              {NAV_STIMULUS_LIBRARY_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavLink
              to={PUBLIC_SCOPE_AND_SEQUENCE}
              onClick={() =>
                handleLinkClick(NAV_EXPLORE, NAV_SCOPE_AND_SEQUENCE_LABEL)
              }
            >
              {NAV_SCOPE_AND_SEQUENCE_LABEL}
            </Styled.MiniNavLink>
          </Box>
          <HrStyled marginTop={18} marginBottom={spacing24} />
          <Styled.MiniNavHeading>{NAV_PRODUCT_LABEL}</Styled.MiniNavHeading>
          <Box $flexWrap="wrap">
            <Styled.MiniNavLink
              to={PUBLIC_TEACHER_BENEFITS}
              onClick={() =>
                handleLinkClick(NAV_PRODUCT, NAV_FOR_TEACHERS_LABEL)
              }
            >
              {NAV_FOR_TEACHERS_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavLink
              to={PUBLIC_SCHOOL_BENEFITS}
              onClick={() =>
                handleLinkClick(NAV_PRODUCT, NAV_FOR_SCHOOLS_LABEL)
              }
            >
              {NAV_FOR_SCHOOLS_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavLink
              to={PUBLIC_ABOUT}
              onClick={() => handleLinkClick(NAV_PRODUCT, NAV_ABOUT_LABEL)}
            >
              {NAV_ABOUT_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavLink
              to={PUBLIC_PRICING}
              onClick={() => handleLinkClick(NAV_PRODUCT, NAV_PRICING_LABEL)}
            >
              {NAV_PRICING_LABEL}
            </Styled.MiniNavLink>
          </Box>
          <HrStyled marginTop={18} marginBottom={spacing24} />
          <Styled.MiniNavHeading>{NAV_SUPPORT_LABEL}</Styled.MiniNavHeading>
          <Box $flexWrap="wrap">
            <Styled.MiniNavLink
              to={PUBLIC_PRICING_FAQS}
              onClick={() => {
                handleLinkClick(NAV_SUPPORT, NAV_FAQS_LABEL);
                goToFaqs();
              }}
            >
              {NAV_FAQS_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavAnchor
              href={t('helpCentreLink')}
              target="_blank"
              onClick={() =>
                handleLinkClick(NAV_SUPPORT, t('helpCentreLinkLabel'))
              }
            >
              {t('helpCentreLinkLabel')}
            </Styled.MiniNavAnchor>
            <Styled.MiniNavLink
              to={PUBLIC_BLOG}
              onClick={() =>
                handleLinkClick(NAV_SUPPORT, NAV_TEACHING_BLOG_LABEL)
              }
            >
              {NAV_TEACHING_BLOG_LABEL}
            </Styled.MiniNavLink>
            <Styled.MiniNavButton
              type="button"
              className={INTERCOM_LAUNCH_TARGET_CLASS}
              aria-haspopup="dialog"
              onClick={() =>
                handleLinkClick(NAV_SUPPORT, NAV_SEND_MESSAGE_LABEL)
              }
            >
              {NAV_SEND_MESSAGE_LABEL}
            </Styled.MiniNavButton>
          </Box>
          <HrStyled marginTop={18} marginBottom={spacing24} />
        </Styled.MiniNavLinksWrapper>
      </Styled.MiniNavScrollContainer>
      <Styled.MiniNavFooter>
        <PrimaryButton
          data-testid="join-button"
          variant="default"
          onClick={() => {
            pushSideNavFollowDataEvent(JOIN_LINK);
            openChooseUserJoinModal();
          }}
          aria-haspopup="dialog"
        >
          Join
        </PrimaryButton>
        <SecondaryButton
          data-testid="sign-in-button"
          onClick={() => {
            pushSideNavFollowDataEvent(SIGN_IN_LINK);
            openChooseUserSignInModal();
          }}
          aria-haspopup="dialog"
        >
          Sign In
        </SecondaryButton>
      </Styled.MiniNavFooter>
    </Styled.MiniNav>
  );
};

export default MiniNav;
